import React, { useState, useEffect, useContext } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { Card, Stack, CardHeader, Chip } from "@mui/material";
import MaterialTypography from "../../components/MaterialTypography";
import MaterialButton from "../../components/MaterialButton";
import MaterialDataGrid from "../../components/MaterialDataGrid";
import UserDetailsContext from "../../context/auth";
import { fetchAll } from "../../ApiBaseUrl";
import { useLoading } from "../../context/loading";

const DeviceList = () => {
  const navigate = useNavigate();

  const { user, AccessData } = useContext(UserDetailsContext);
  const { circleLoading, setCirleLoading } = useLoading();
  const [pageState, setPageState] = useState({
    loading: false,
    error: false,
    message: "",
    state: "init",
  });

  const [deviceList, setDeviceList] = useState([]);

  const getStatusColor = (lastSeen) => {
    if (!lastSeen) return "default";
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now - lastSeenDate) / (1000 * 60);
    return diffMinutes <= 5 ? "success" : "error";
  };

  const deviceTable = {
    columns: [
      {
        field: "deviceName",
        headerName: "Device Name",
        flex: 1,
        editable: false,
        disableColumnMenu: true,
        sortable: true,
      },
      {
        field: "branch",
        headerName: "Branch",
        flex: 1,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
      },
      {
        field: "direction",
        headerName: "Direction",
        flex: 0.6,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
      },
      {
        field: "isActive",
        headerName: "Enabled",
        flex: 0.5,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value ? "Enabled" : "Disabled"}
            color={params.value ? "info" : "default"}
            size="small"
          />
        ),
      },
      {
        field: "lastSeen",
        headerName: "Machine Status",
        flex: 1.3,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
        renderCell: (params) => {
          const machineTs = params.row.machineLastOnline;
          const apiTs = params.value;
          const bestTs = machineTs || apiTs;
          if (!bestTs)
            return (
              <Chip label="Never Connected" color="default" size="small" />
            );
          const date = new Date(bestTs);
          const isOnline = getStatusColor(bestTs) === "success";
          return (
            <Stack direction="column" spacing={0.2}>
              <Chip
                label={isOnline ? "Machine ON" : "Machine OFF"}
                color={isOnline ? "success" : "error"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              <span style={{ fontSize: "11px", color: "#888" }}>
                {machineTs
                  ? `Heartbeat: ${date.toLocaleString()}`
                  : `Last API: ${date.toLocaleString()}`}
              </span>
            </Stack>
          );
        },
      },
      {
        field: "apiKey",
        headerName: "API Key",
        flex: 1,
        disableColumnMenu: true,
        editable: false,
        sortable: false,
      },
    ],
    rows: deviceList.map((data) => ({
      id: data.id,
      deviceName: data.deviceName,
      branch: data.branch,
      direction: data.direction,
      isActive: data.isActive,
      lastSeen: data.lastSeen,
      machineLastOnline: data.machineLastOnline,
      apiKey: data.apiKey,
      cloudId: data.cloudId,
      notes: data.notes,
    })),
  };

  const handleNewDevice = () => {
    navigate("/device/add-device");
  };

  const onDeviceRowClick = (e) => {
    if (e?.id) {
      navigate(`/device/add-device`, { state: e?.id });
    }
  };

  useEffect(() => {
    const fetchDevices = async () => {
      setCirleLoading(true);
      try {
        const fields = [
          "id",
          "deviceName",
          "branch",
          "direction",
          "cloudId",
          "isActive",
          "lastSeen",
          "machineLastOnline",
          "apiKey",
          "notes",
        ];
        const data = await fetchAll("RegisteredDevice", fields);
        setDeviceList(data?.allRegisteredDevice || []);
      } catch (error) {
        console.error("Error fetching devices:", error);
      } finally {
        setCirleLoading(false);
      }
    };

    fetchDevices();
  }, []);

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack direction="column" spacing={0}>
                <MaterialTypography variant="h5" fontWeight="medium">
                  Device Management
                </MaterialTypography>
              </Stack>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleNewDevice()}
              >
                Add Device
              </MaterialButton>
            </Stack>
          }
        />
        <MaterialDataGrid
          rows={deviceTable?.rows}
          columns={deviceTable.columns}
          checkboxSelection={false}
          isSelectable={false}
          loading={pageState.loading}
          onRowClick={onDeviceRowClick}
          localeText={{
            noRowsLabel: (
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <MaterialTypography
                  variant="body2"
                  fontWeight="medium"
                  color="text"
                >
                  No devices registered
                </MaterialTypography>
                <MaterialTypography variant="caption" color="text">
                  Click on the "Add Device" button to register a new biometric
                  device.
                </MaterialTypography>
              </Stack>
            ),
          }}
        />
      </Card>
    </DashboardLayout>
  );
};

export default DeviceList;
